import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI, organizationAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Building2 } from 'lucide-react';
import './Login.css';

const UnifiedLogin = () => {
  const [activeTab, setActiveTab] = useState('admin'); // 'admin', 'employee', 'manager'
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgLoading, setOrgLoading] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Load current organization from session on mount
  useEffect(() => {
    loadCurrentOrganization();
  }, []);

  const loadCurrentOrganization = async () => {
    try {
      const currentOrgResponse = await organizationAPI.getCurrentOrganization();
      if (currentOrgResponse.data.organization) {
        setSelectedOrg(currentOrgResponse.data.organization.code);
      }
    } catch (err) {
      // No current org, that's fine
    } finally {
      setOrgLoading(false);
    }
  };

  const switchOrganization = async (orgCode) => {
    if (!orgCode) return;
    
    try {
      const response = await organizationAPI.switchOrganization(orgCode);
      setSelectedOrg(orgCode);
      console.log('✅ Switched to organization:', orgCode);
    } catch (error) {
      console.error('Failed to switch organization:', error);
      toast.error('Failed to switch organization');
    }
  };

  const handleOrgChange = async (e) => {
    const orgCode = e.target.value.trim().toUpperCase();
    setSelectedOrg(orgCode);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Clear form when switching tabs
    setFormData({ email: '', password: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate organization code is entered
    if (!selectedOrg || selectedOrg.trim() === '') {
      toast.error('Please enter your organization code');
      return;
    }

    // Switch to the organization before login
    await switchOrganization(selectedOrg);

    setLoading(true);

    try {
      let response;
      let redirectPath;

      // Call appropriate API based on active tab
      switch (activeTab) {
        case 'admin':
          console.log('🔵 Admin login attempt:', formData.email);
          response = await authAPI.adminLogin(formData);
          redirectPath = '/admin/dashboard';
          break;
        case 'employee':
          console.log('🔵 Employee login attempt:', formData.email);
          response = await authAPI.employeeLogin(formData);
          redirectPath = '/employee/dashboard';
          break;
        case 'manager':
          console.log('🔵 Manager login attempt:', formData.email);
          response = await authAPI.managerLogin(formData);
          redirectPath = '/manager/dashboard';
          break;
        default:
          throw new Error('Invalid login type');
      }

      console.log('✅ Login response received:', response.data);
      const { user, token } = response.data;
      
      console.log('🔵 Calling login with user:', user);
      login(user, token);
      toast.success('Login successful!');
      console.log('🔵 Navigating to', redirectPath);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      console.error('❌ Login error:', error);
      const message = error.response?.data?.error || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getHeaderInfo = () => {
    switch (activeTab) {
      case 'admin':
        return {
          title: 'Admin Login',
          subtitle: 'Sign in to access the admin dashboard'
        };
      case 'employee':
        return {
          title: 'Employee Login',
          subtitle: 'Sign in to access your employee portal'
        };
      case 'manager':
        return {
          title: 'Manager Login',
          subtitle: 'Sign in to access your department dashboard'
        };
      default:
        return { title: 'Login', subtitle: '' };
    }
  };

  const headerInfo = getHeaderInfo();

  return (
    <div className="login-container">
      <div className="login-card unified-login-card">
        {/* Tab Selection */}
        <div className="login-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => handleTabChange('admin')}
          >
            Admin
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'employee' ? 'active' : ''}`}
            onClick={() => handleTabChange('employee')}
          >
            Employee
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'manager' ? 'active' : ''}`}
            onClick={() => handleTabChange('manager')}
          >
            Manager
          </button>
        </div>

        <div className="login-header">
          <h2>{headerInfo.title}</h2>
          <p>{headerInfo.subtitle}</p>
        </div>

        {/* Organization Code Input */}
        <div className="form-group org-selection">
          <label htmlFor="organization">
            <Building2 size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Organization Code
          </label>
          <input
            type="text"
            id="organization"
            value={selectedOrg}
            onChange={handleOrgChange}
            required
            disabled={orgLoading}
            placeholder="Enter organization code (e.g., ORG001)"
            className="org-input-login"
            style={{ textTransform: 'uppercase' }}
          />
          <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            Contact your administrator if you don't know your organization code
          </small>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginBottom: '15px' }}>
            <Link
              to={`/${activeTab}/forgot-password?org_code=${encodeURIComponent(selectedOrg || '')}`} 
              style={{ color: '#4CAF50', fontSize: '14px' }}
            >
              Forgot Password?
            </Link>
          </div>

          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <Link to="/">Back to Home</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLogin;